import { useEffect } from 'react';
import './ThunderWaveAnimation.css';

export default function ThunderWaveAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get positions
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentRef.current.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;
    
    // Distance and angle calculations
    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements
    const elements = [];
    const timers = [];

    // Player charging animation
    playerEl.classList.add('wave-charge');
    
    // Create initial glow at player
    const initialGlow = document.createElement('div');
    initialGlow.className = 'wave-initial-glow';
    initialGlow.style.left = `${startX}px`;
    initialGlow.style.top = `${startY}px`;
    battleBg.appendChild(initialGlow);
    elements.push(initialGlow);

    // Create electric arcs after short delay
    const arcTimer = setTimeout(() => {
      // Main wave container
      const waveContainer = document.createElement('div');
      waveContainer.className = 'wave-container';
      
      // Position and rotate the container to point at opponent
      waveContainer.style.left = `${startX}px`;
      waveContainer.style.top = `${startY}px`;
      waveContainer.style.width = `${distance}px`;
      waveContainer.style.transform = `rotate(${angle}rad)`;
      
      battleBg.appendChild(waveContainer);
      elements.push(waveContainer);
      
      // Create multiple electric arcs inside the container with slight time offsets
      for (let i = 0; i < 5; i++) {
        const arcTimer = setTimeout(() => {
          const arc = document.createElement('div');
          arc.className = 'thunder-wave-arc';
          
          // Set random height for wave effect
          arc.style.setProperty('--wave-height', `${8 + Math.random() * 15}px`);
          
          waveContainer.appendChild(arc);
          elements.push(arc);
          
          // Remove arc after animation
          const removeArcTimer = setTimeout(() => {
            if (arc.isConnected) {
              arc.remove();
            }
          }, 700);
          timers.push(removeArcTimer);
        }, i * 100); // Staggered creation
        timers.push(arcTimer);
      }
      
      // Play impact effect after waves reach opponent
      const impactTimer = setTimeout(() => {
        // Create paralysis ring effect
        for (let i = 0; i < 2; i++) {
          const paralysisRing = document.createElement('div');
          paralysisRing.className = 'paralysis-ring';
          paralysisRing.style.left = `${endX}px`;
          paralysisRing.style.top = `${endY}px`;
          paralysisRing.style.animationDelay = `${i * 200}ms`;
          
          battleBg.appendChild(paralysisRing);
          elements.push(paralysisRing);
          
          // Remove ring after animation
          const removeRingTimer = setTimeout(() => {
            if (paralysisRing.isConnected) {
              paralysisRing.remove();
            }
          }, 1000);
          timers.push(removeRingTimer);
        }
        
        // Create electric sparks around opponent
        for (let i = 0; i < 10; i++) {
          const sparkTimer = setTimeout(() => {
            const spark = document.createElement('div');
            spark.className = 'thunder-wave-spark';
            
            // Position randomly around opponent
            const sparkAngle = Math.random() * Math.PI * 2;
            const sparkDistance = opponentRect.width * 0.3 + Math.random() * opponentRect.width * 0.3;
            spark.style.left = `${endX + Math.cos(sparkAngle) * sparkDistance}px`;
            spark.style.top = `${endY + Math.sin(sparkAngle) * sparkDistance}px`;
            
            // Random size and rotation
            const size = 4 + Math.random() * 8;
            spark.style.width = `${size}px`;
            spark.style.height = `${size}px`;
            
            battleBg.appendChild(spark);
            elements.push(spark);
            
            // Remove spark after animation
            const removeSparkTimer = setTimeout(() => {
              if (spark.isConnected) {
                spark.remove();
              }
            }, 600);
            timers.push(removeSparkTimer);
          }, Math.random() * 400); // Random timing
          timers.push(sparkTimer);
        }
        
        // Make opponent flash and apply paralysis effect
        opponentEl.classList.add('wave-hit');
        
        // Apply paralysis visual indicator (yellow electricity) to opponent
        const paralysisEffect = document.createElement('div');
        paralysisEffect.className = 'paralysis-effect';
        
        // Position on opponent
        paralysisEffect.style.left = `${endX}px`;
        paralysisEffect.style.top = `${endY}px`;
        
        battleBg.appendChild(paralysisEffect);
        elements.push(paralysisEffect);
        
        // Remove paralysis visual after animation
        const removeParalysisTimer = setTimeout(() => {
          if (paralysisEffect.isConnected) {
            paralysisEffect.remove();
          }
        }, 1200);
        timers.push(removeParalysisTimer);
        
        // Reset opponent animation
        const resetOpponentTimer = setTimeout(() => {
          opponentEl.classList.remove('wave-hit');
        }, 1000);
        timers.push(resetOpponentTimer);
        
      }, 400); // Impact happens after waves travel
      timers.push(impactTimer);
      
    }, 600); // Delay before arcs appear
    timers.push(arcTimer);
    
    // Reset player animation
    const resetPlayerTimer = setTimeout(() => {
      playerEl.classList.remove('wave-charge');
    }, 800);
    timers.push(resetPlayerTimer);

    // Clean up and notify parent after animation completes
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2200);
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.isConnected) {
          element.remove();
        }
      });
      
      // Reset any modified styles/classes
      if (playerEl) {
        playerEl.classList.remove('wave-charge');
      }
      if (opponentEl) {
        opponentEl.classList.remove('wave-hit');
      }
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

ThunderWaveAnimation.moveType = "electric";
